import { useState, useEffect, useRef } from 'react';
import { safeFetchJSON } from '../utils/apiHelpers';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Send, Loader2, MessageCircle, Mic, User, Brain, Sparkles, TrendingUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ScrollToBottom from 'react-scroll-to-bottom';
import clsx from 'clsx';
import { OwenMarkdown } from '../components/OwenMarkdown';
import useStreaming from '../hooks/useStreaming';
// 🎯 GOUVERNANCE PÉDAGOGIQUE: Import du bandeau objectif
import ActiveObjectiveBanner from '../components/ActiveObjectiveBanner';
// 🔒 Mars 2026: Hook centralisé pour l'objectif actif
import useActiveObjective from '../hooks/useActiveObjective';
// 🔄 SYNCHRONISATION: Import des événements pédagogiques
import { notifyEvaluationComplete } from '../utils/pedagogicalEvents';
// 🔒 P0 EXECUTION CONTEXT: Arrêt des tâches à la sortie de page
import { usePageExecutionContext } from '../hooks/usePageExecutionContext';
// 🔒 P0 UX MOA (21 Jan 2026): Contrôle sessions pour mode écrit
import { useSessionCredits } from '../hooks/useSessionCredits';
import { SessionsExhaustedModal } from '../components/premium/SessionsExhaustedModal';

// Secure backend URL with fallback
import { getBackendUrl, getApiUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const EntrainementEcrit = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // 🔒 P0 UX MOA (21 Jan 2026): Contrôle sessions pour mode écrit
  const { isExhausted: sessionsExhausted, refresh: refreshSessions, loading: sessionsLoading } = useSessionCredits();
  const [showSessionsExhaustedModal, setShowSessionsExhaustedModal] = useState(false);
  
  // 🔒 P0 EXECUTION CONTEXT: Arrêt des tâches backend à la sortie de page
  usePageExecutionContext('entrainement-ecrit');
  
  const [module, setModule] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(false); // Changed to false for instant UI display
  const [scenarioLoading, setScenarioLoading] = useState(true); // New state for background scenario loading
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [showDifficultySelector, setShowDifficultySelector] = useState(false);
  
  // 🎯 GOUVERNANCE PÉDAGOGIQUE: Objectif actif (hook centralisé — SOURCE UNIQUE)
  const { activeObjective, handleObjectiveLoaded } = useActiveObjective();
  
  // Owen 360° Recommendations
  const [recommendations, setRecommendations] = useState([]);
  const [showRecommendations, setShowRecommendations] = useState(false);
  
  // Owen 360° Proactive Intro Message (Phase 4)
  const [proactiveIntroMessage, setProactiveIntroMessage] = useState(null);
  
  // Streaming state
  const { streamedText, isStreaming, startStream, cancelStream, resetStream } = useStreaming({
    onComplete: (fullText) => {
      // Guard: ne pas ajouter de message vide (streaming sans données)
      if (!fullText || !fullText.trim()) {
        console.warn('[EntrainementEcrit] ⚠️ Streaming completed with empty text, skipping message add');
        setIsTyping(false);
        resetStream();
        return;
      }
      // Add completed message to conversation
      const aiMessage = {
        role: 'assistant',
        content: fullText,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, aiMessage]);
      setIsTyping(false);
      resetStream();
    },
    onError: (error) => {
      console.error('Streaming error:', error);
      // Ne pas afficher de toast ici — le fallback non-streaming gère l'erreur
      // Le toast sera affiché uniquement si le fallback échoue aussi (catch externe)
    },
    onStart: () => {
      console.log('🌊 Streaming started');
    }
  });
  
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // 🔒 P0 UX MOA (21 Jan 2026): Attendre que le hook sessions soit chargé
    if (sessionsLoading) {
      console.log('[EntrainementEcrit] ⏳ Waiting for sessions to load...');
      return;
    }
    
    // 🔒 P0 UX MOA (21 Jan 2026): GUARD SESSIONS ÉPUISÉES - Bloquer toute initialisation IA
    if (sessionsExhausted) {
      console.log('[EntrainementEcrit] 🔒 BLOCKED - Sessions épuisées, pas d\'initialisation IA');
      setShowSessionsExhaustedModal(true);
      return; // STOP - aucun appel backend
    }
    
    fetchModuleData();
  }, [moduleId, user, navigate, sessionsExhausted, sessionsLoading]);

  useEffect(() => {
    scrollToBottom();
  }, [messages, streamedText]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // 🔧 FIX MOBILE: VisualViewport pour gérer le clavier Android/iOS
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);
  
  useEffect(() => {
    const initialHeight = window.innerHeight;
    
    const updateAppHeight = () => {
      const vh = window.visualViewport 
        ? window.visualViewport.height 
        : window.innerHeight;
      document.documentElement.style.setProperty('--app-height', `${vh}px`);
      // Clavier détecté si le viewport a rétréci de plus de 150px
      setIsKeyboardVisible(initialHeight - vh > 150);
    };
    
    updateAppHeight();
    
    // Bloquer le scroll du body sur cette page (empêche le browser de scroller le bandeau hors écran)
    const htmlEl = document.documentElement;
    const bodyEl = document.body;
    const prevHtmlOverflow = htmlEl.style.overflow;
    const prevHtmlHeight = htmlEl.style.height;
    const prevBodyOverflow = bodyEl.style.overflow;
    const prevBodyHeight = bodyEl.style.height;
    
    htmlEl.style.overflow = 'hidden';
    htmlEl.style.height = '100%';
    bodyEl.style.overflow = 'hidden';
    bodyEl.style.height = '100%';
    
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateAppHeight);
    } else {
      window.addEventListener('resize', updateAppHeight);
    }
    
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateAppHeight);
      } else {
        window.removeEventListener('resize', updateAppHeight);
      }
      htmlEl.style.overflow = prevHtmlOverflow;
      htmlEl.style.height = prevHtmlHeight;
      bodyEl.style.overflow = prevBodyOverflow;
      bodyEl.style.height = prevBodyHeight;
      document.documentElement.style.removeProperty('--app-height');
    };
  }, []);

  const fetchModuleData = async () => {
    try {
      setScenarioLoading(true);
      const response = await axios.get(`${API}/modules-v2/${moduleId}/content`);
      const { module: moduleData, scenarios } = response.data;
      setModule(moduleData);
      
      // Generate immersive scenario in background (non-blocking)
      generateImmersiveScenario(moduleData);
      
    } catch (error) {
      toast.error('Erreur lors du chargement du module');
      console.error(error);
      setScenarioLoading(false);
    }
  };
  
  const generateImmersiveScenario = async (moduleData) => {
    try {
      console.log('🎬 Generating immersive scenario for module:', moduleId);
      // Don't block UI, scenario loads in background
      
      // Call the new immersive scenario endpoint
      // Send module_id as query parameter
      console.log('📤 Calling immersive scenario API with module_id:', moduleId);
      
      const response = await axios.post(
        `${API}/coach/text-immersive-scenario?module_id=${moduleId}`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      console.log('📊 Immersive scenario response:', response.data);
      
      if (response.data.status === 'success' && response.data.scenario) {
        const immersiveScenario = response.data.scenario;
        setScenario(immersiveScenario);
        
        console.log('✅ Displaying immersive scenario:', immersiveScenario.scenario_title);
        // Display the immersive scenario with full narrative structure
        displayImmersiveScenario(immersiveScenario);
        setScenarioLoading(false);
      } else {
        console.warn('⚠️ No scenario in response, using fallback');
        // Fallback to generic welcome if generation fails
        displayGenericWelcome(moduleData);
        setScenarioLoading(false);
      }
    } catch (error) {
      console.error('❌ Error generating immersive scenario:', error);
      console.error('Error details:', error.response?.data || error.message);
      // Fallback to generic welcome
      displayGenericWelcome(moduleData);
      setScenarioLoading(false);
    }
  };
  
  const displayImmersiveScenario = (immersiveScenario) => {
    // Get role-specific visual styling
    const roleConfig = getRoleConfig(immersiveScenario.owen_role_type);
    
    // Build the immersive introduction message (clean, no separators)
    const introMessage = `${roleConfig.emoji} **${immersiveScenario.scenario_title}**

📖 **Contexte**
${immersiveScenario.context}

🎯 **Objectif**
${immersiveScenario.objective}

🎭 **Les Rôles**
${roleConfig.owenLabel} **Owen** : ${immersiveScenario.owen_role}
${roleConfig.userLabel} **Toi** : ${immersiveScenario.user_role}

⚡ **Déclencheur**
${immersiveScenario.trigger}

💬 **${getRoleTitle(immersiveScenario.owen_role_type)}**

${immersiveScenario.interaction_start}

✨ *La conversation commence maintenant. Réponds naturellement à Owen.*`;

    setMessages([
      {
        role: 'assistant',
        content: introMessage,
        timestamp: new Date().toISOString(),
        isImmersiveIntro: true,
        roleType: immersiveScenario.owen_role_type
      }
    ]);
  };
  
  const getRoleConfig = (roleType) => {
    const configs = {
      superior: {
        emoji: '👔',
        separator: '',
        owenLabel: '🎯',
        userLabel: '👤',
        color: '#8B5CF6'
      },
      colleague: {
        emoji: '🤝',
        separator: '',
        owenLabel: '👥',
        userLabel: '👤',
        color: '#10B981'
      },
      collaborator: {
        emoji: '💼',
        separator: '',
        owenLabel: '👷',
        userLabel: '👤',
        color: '#F59E0B'
      }
    };
    
    return configs[roleType] || configs.collaborator;
  };
  
  const getRoleTitle = (roleType) => {
    const titles = {
      superior: 'Owen - Ton mentor',
      colleague: 'Owen - Ton collègue',
      collaborator: 'Owen - Membre de ton équipe'
    };
    
    return titles[roleType] || 'Owen';
  };
  
  const getRoleBehavior = (roleType) => {
    const behaviors = {
      superior: 'Mentor bienveillant mais exigeant. Tu challenges le manager tout en montrant confiance. Tu partages ton expérience, poses des questions stratégiques, et invites à élever la réflexion. Ne sois pas condescendant.',
      colleague: 'Pair engagé et ouvert. Tu cherches une collaboration win-win. Tu partages tes préoccupations, proposes des idées, et cherches l\'alignement mutuel. Ne sois pas compétitif ni distant.',
      collaborator: 'Membre d\'équipe sincère et transparent. Tu exprimes tes besoins, doutes et attentes. Tu partages ton vécu et attends du soutien managérial. Ne sois pas passif-agressif, reste constructif.'
    };
    
    return behaviors[roleType] || behaviors.collaborator;
  };
  
  const displayGenericWelcome = (moduleData) => {
    setMessages([
      {
        role: 'assistant',
        content: `Bienvenue dans l'entraînement écrit du module **${moduleData.title}**.\n\nJe suis Owen, votre coach IA. Partagez-moi une situation concrète de votre quotidien managérial, et explorons ensemble comment appliquer les concepts de ce module.\n\nPar où souhaitez-vous commencer ?`,
        timestamp: new Date().toISOString()
      }
    ]);
  };
  
  const fetchRecommendations = async () => {
    if (!user?.user_id) return;
    
    try {
      // Mock data for UI validation (same as Dashboard)
      const mockRecommendations = [
        {
          type: "emotion_based",
          module_id: "9",
          module_title: "Gérer son stress managérial",
          reason: "Pour gérer votre stress récurrent",
          relevance_score: 0.92
        },
        {
          type: "progression_based",
          module_id: "7",
          module_title: "Savoir dire non",
          reason: "Renforcer votre assertivité",
          relevance_score: 0.85
        }
      ];
      
      // Try real API first
      try {
        const response = await axios.get(
          `${API}/ai/recommendations/${user.user_id}?max_recommendations=2`,
          {
            headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
            timeout: 2000
          }
        );
        
        if (response.data.recommendations && response.data.recommendations.length > 0) {
          setRecommendations(response.data.recommendations.slice(0, 2));
        } else {
          setRecommendations(mockRecommendations);
        }
      } catch (apiError) {
        setRecommendations(mockRecommendations);
      }
    } catch (error) {
      console.error('⚠️ Error loading recommendations:', error);
    }
  };
  
  useEffect(() => {
    // Fetch recommendations on mount and when user starts a new session
    if (user && scenario) {
      fetchRecommendations();
      fetchProactiveIntroMessage();
    }
  }, [user, scenario]);
  
  const fetchProactiveIntroMessage = async () => {
    if (!user?.user_id) return;
    
    try {
      const response = await axios.post(
        `${API}/ai/predictive-feedback`,
        {
          user_id: user.user_id,
          context: "session_start"
        },
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          timeout: 3000
        }
      );
      
      if (response.data.feedback_type === "encouragement" && response.data.message) {
        setProactiveIntroMessage(response.data.message);
      }
    } catch (error) {
      console.log('⚠️ No proactive intro message');
    }
  };
  
  const startScenario = (scenarioData, difficulty = null) => {
    setSelectedDifficulty(difficulty);
    setShowDifficultySelector(false);
    
    // Extract scenario data - use prompt as main content
    const prompt = scenarioData.prompt || scenarioData.context || '';
    const goal = scenarioData.goal || '';
    const title = scenarioData.title || scenarioData.scenario_title || 'Scénario d\'entraînement';
    
    // Build initial message
    let initialMessage = `🎯 **${title}**\n\n`;
    
    // Add the scenario prompt/context
    if (prompt) {
      initialMessage += `${prompt}\n\n`;
    }
    
    // Add goal if available
    if (goal) {
      initialMessage += `**Objectif** : ${goal}\n\n`;
    }
    
    // Add difficulty info if selected
    if (difficulty) {
      initialMessage += `**Niveau choisi** : ${difficulty.level}\n\n`;
      if (difficulty.coaching_approach) {
        initialMessage += `${difficulty.coaching_approach}\n\n`;
      }
    }
    
    initialMessage += `\nCommençons cet entraînement. Comment souhaitez-vous aborder cette situation ?`;
    
    // Prepend proactive intro if available
    if (proactiveIntroMessage) {
      initialMessage = `💬 **Owen** : ${proactiveIntroMessage}\n\n${initialMessage}`;
    }
    
    setMessages([
      {
        role: 'assistant',
        content: initialMessage,
        timestamp: new Date().toISOString()
      }
    ]);
  };

  const handleDifficultyChoice = (difficultyIndex) => {
    if (scenario && scenario.difficulty_levels && scenario.difficulty_levels[difficultyIndex]) {
      const difficulty = scenario.difficulty_levels[difficultyIndex];
      startScenario(scenario, difficulty);
    }
  };

  const sendMessage = async () => {
    if (!userInput.trim() || isTyping || isStreaming) return;
    
    // 🔒 P0 UX MOA (21 Jan 2026): GUARD SESSIONS ÉPUISÉES - Bloquer envoi si crédit = 0
    if (sessionsExhausted) {
      console.log('[EntrainementEcrit] 🔒 BLOCKED sendMessage - Sessions épuisées');
      setShowSessionsExhaustedModal(true);
      return; // STOP - aucun appel backend
    }

    const userMessage = {
      role: 'user',
      content: userInput,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    const currentInput = userInput; // Save for error recovery
    setUserInput('');
    setIsTyping(true);

    try {
      // Build conversation context
      const context = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      // Build system prompt with scenario context
      let systemContext = '';
      
      if (scenario && scenario.owen_role && scenario.owen_role_type) {
        // Use immersive scenario with role context
        systemContext = `${scenario.owen_role}

Tu incarnes ce rôle de manière authentique et cohérente tout au long de la conversation.

**Contexte du scénario** :
${scenario.context}

**Objectif pédagogique** :
${scenario.objective}

**Ton rôle** : ${getRoleBehavior(scenario.owen_role_type)}

**Mission du manager** :
${scenario.manager_mission}

Maintiens ce rôle et cette posture dans toutes tes réponses. Sois naturel, humain et cohérent avec le personnage que tu incarnes.`;
      } else if (scenario) {
        // Fallback to old scenario format
        systemContext = `Tu es Owen, coach IA du programme Coach DIA pour le module "${module?.title || 'formation managériale'}".`;
        
        if (scenario.prompt) {
          systemContext += `\n\n${scenario.prompt}`;
        }
        
        if (selectedDifficulty) {
          systemContext += `\n\nNiveau de difficulté actuel: ${selectedDifficulty.level}`;
          if (selectedDifficulty.coaching_approach) {
            systemContext += `\nApproche de coaching: ${selectedDifficulty.coaching_approach}`;
          }
        }
      } else {
        // Generic coach context
        systemContext = `Tu es Owen, coach IA du programme Coach DIA pour le module "${module?.title || 'formation managériale'}". Accompagne le manager avec empathie et expertise.`;
      }

      // Prepare request body
      const requestBody = {
        user_id: user.user_id || user.email || 'anonymous',
        module_id: `M${moduleId}`,
        input_text: currentInput,
        context: context,
        system_context: systemContext // Pass scenario context
      };

      // Try streaming first, fallback to non-streaming if error
      try {
        console.log('🌊 Starting streaming request...');
        // 🔒 P0 FIX: Add X-Execution-Context-Id header manually because useStreaming uses nativeFetch
        const executionContextId = window.__currentExecutionContextId;
        await startStream(
          `${API}/ai/text-training-session/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              ...(executionContextId && { 'X-Execution-Context-Id': executionContextId })
            },
            body: JSON.stringify(requestBody)
          }
        );
      } catch (streamError) {
        console.warn('⚠️ Streaming failed, falling back to standard request:', streamError);
        
        // Fallback to non-streaming endpoint (avec auth header explicite)
        const response = await axios.post(`${API}/ai/text-training-session`, requestBody, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        const aiMessage = {
          role: 'assistant',
          content: response.data.reply_text,
          timestamp: response.data.timestamp || new Date().toISOString()
        };

        setMessages(prev => [...prev, aiMessage]);
        setIsTyping(false);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Extract detailed error message and ensure it's a string
      let detail = 'Erreur inconnue';
      
      if (error.response) {
        // Server responded with error
        const errorData = error.response.data?.detail || error.response.data?.message;
        
        // Handle Pydantic validation errors (422) which return array of objects
        if (Array.isArray(errorData)) {
          // Convert validation errors to readable string
          detail = errorData.map(err => {
            const field = err.loc ? err.loc.join('.') : 'field';
            const message = err.msg || 'invalid';
            return `${field}: ${message}`;
          }).join(', ');
        } else if (typeof errorData === 'object' && errorData !== null) {
          // Handle other object errors
          detail = JSON.stringify(errorData);
        } else if (typeof errorData === 'string') {
          detail = errorData;
        } else {
          detail = `Erreur ${error.response.status}`;
        }
      } else if (error.request) {
        // Request made but no response
        detail = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
      } else {
        // Something else happened
        detail = error.message || 'Erreur inconnue';
      }
      
      const errorMessage = {
        role: 'assistant',
        content: `⚠️ Désolé, une erreur est survenue : ${detail}`,
        timestamp: new Date().toISOString()
      };
      
      setMessages(prev => [...prev, errorMessage]);
      toast.error(String(detail));
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // ✅ ÉTAPE 5: End session and adjust pathway (written mode) V7.1
  const endWrittenSession = async () => {
    if (isTyping) {
      toast.warning('Attends la fin de l\'interaction en cours...');
      return;
    }
    
    setIsTyping(true);
    
    try {
      console.log('[endWrittenSession V7.1] 🎯 Ending written session with V7.1 feedback');
      
      // ✅ V7.1: Prepare conversation history for feedback
      const conversationHistory = messages.map(msg => ({
        role: msg.role,
        content: msg.content
      }));
      
      const userName = user?.firstName || user?.user_name || 'Manager';
      const moduleName = module?.title || 'Coach DIA';
      
      // ✅ V7.1: Call the new feedback endpoint
      // ✅ ÉTAPE 4: 🔒 FEEDBACK DÉCISIONNEL OWEN (VERROUILLAGE QUALIOPI)
      // Génère une DÉCISION pédagogique avec évaluation critère par critère
      console.log('[endWrittenSession] 🔒 Generating decisional feedback with Owen...');
      
      const sessionId = `written_${Date.now()}`;
      const token = localStorage.getItem('token');
      
      try {
        const decisionalResponse = await axios.post(
          `${API}/pedagogical/session/decisional-feedback`,
          {
            module_id: parseInt(moduleId) || 0,
            session_id: sessionId,
            session_type: 'written',
            conversation_history: conversationHistory,
            // 🔒 D2 MOA (Déc 2025): Transmettre objective_id pour cohérence universelle
            objective_id: activeObjective?.id || null
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        console.log('[endWrittenSession] 🔒 D2 MOA: objective_id transmis:', activeObjective?.id);
        console.log('[endWrittenSession] 🔒 Decisional feedback received:', decisionalResponse.data);
        
        // Afficher le feedback décisionnel formaté
        const feedbackContent = decisionalResponse.data.formatted_feedback || 
          decisionalResponse.data.decision?.formatted_feedback ||
          "Session terminée. Merci pour votre engagement.";
        
        const feedbackMessage = {
          role: 'assistant',
          content: feedbackContent,
          timestamp: new Date().toISOString(),
          isDecisionalFeedback: true
        };
        
        setMessages(prev => [...prev, feedbackMessage]);
        
        // Notifications selon le résultat
        const progressPercent = decisionalResponse.data.progress_percent || 0;
        const isValidated = decisionalResponse.data.is_objective_validated;
        
        if (isValidated) {
          toast.success('✅ Objectif validé par Owen !', { duration: 5000 });
        } else if (progressPercent >= 60) {
          toast.info(`📊 Progression: ${progressPercent}%`, { duration: 3000 });
        }
        
        // 🔄 SYNCHRONISATION: Notifier les composants
        notifyEvaluationComplete({
          module_id: parseInt(moduleId),
          progress_percent: progressPercent,
          is_objective_validated: isValidated
        });
        
        console.log('[endWrittenSession] 🔒 Decisional feedback displayed successfully');
        toast.success('Session terminée avec succès !');
        
      } catch (decisionalError) {
        console.error('[endWrittenSession] ⚠️ Decisional feedback error:', decisionalError);
        
        // 🔒 MIGRATION P0: Plus de fallback vers l'ancien système
        // Le feedback décisionnel est la SEULE source de vérité (Qualiopi)
        console.log('[endWrittenSession] 🔒 Using minimal fallback (no legacy system)');
        
        // Message minimal informatif
        const minimalFeedbackMessage = {
          role: 'assistant',
          content: `📊 **Session terminée**\n\nMerci ${userName} pour cette session d'entraînement !\n\nVotre progression a été enregistrée. Continuez à vous entraîner pour valider les objectifs du module.`,
          timestamp: new Date().toISOString()
        };
        
        setMessages(prev => [...prev, minimalFeedbackMessage]);
        toast.info('Session terminée', { duration: 3000 });
      }
      
      // ✅ ÉTAPE 5: 🎓 QUALIOPI - Évaluation pédagogique Owen (CRITIQUE - Backup)
      // Owen analyse automatiquement la session et met à jour les objectifs
      try {
        console.log('[endWrittenSession] 🎓 Triggering Owen pedagogical evaluation (backup)...');
        const evalResponse = await axios.post(
          `${API}/pedagogical/internal/evaluate`,
          {
            user_id: user.user_id || user.email,
            module_id: parseInt(moduleId) || 0,
            session_id: sessionId,
            session_type: 'written',
            conversation_history: conversationHistory
          },
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        console.log('[endWrittenSession] 🎓 Owen evaluation complete:', evalResponse.data);
        
        // 🔄 SYNCHRONISATION: Notifier les composants que l'évaluation est terminée
        notifyEvaluationComplete({
          module_id: parseInt(moduleId),
          ...evalResponse.data
        });
        
        if (evalResponse.data.module_newly_validated) {
          toast.success('🏆 Module validé par Owen !', { duration: 5000 });
        } else if (evalResponse.data.new_evidences_count > 0) {
          toast.info(`📚 ${evalResponse.data.new_evidences_count} preuve(s) collectée(s)`, { duration: 3000 });
        }
      } catch (evalError) {
        console.error('[endWrittenSession] ⚠️ Owen evaluation error (non-blocking):', evalError);
        // Non-blocking: ne pas empêcher la fin de session
      }
      
      // ✅ ÉTAPE 6: Adjust pathway after session
      await adjustPathwayAfterWrittenSession(user.user_id || user.email, moduleId, conversationHistory);
      
    } catch (error) {
      console.error('[endWrittenSession V7.1] Error ending session:', error);
      toast.error('Erreur lors de la génération du feedback', { duration: 5000 });
      
      // Add fallback message
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Merci pour cette session de coaching ! 🙏\n\nContinuez à pratiquer les concepts du module "${module?.title}".\n\n✅ Session terminée.`,
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsTyping(false);
    }
  };
  
  // ✅ ÉTAPE 5: Adjust pathway after written session
  const adjustPathwayAfterWrittenSession = async (userId, moduleId, conversationMessages) => {
    try {
      console.log('[adjustPathway] 🎯 Adjusting pathway after written session');
      
      // Calculate session duration
      const sessionDuration = conversationMessages.length > 0 
        ? (new Date(conversationMessages[conversationMessages.length - 1].timestamp) - 
           new Date(conversationMessages[0].timestamp)) / (1000 * 60) // minutes
        : 0;
      
      const sessionData = {
        emotions: [], // No emotion tracking in written mode
        topics: [], // Could extract from conversation content
        duration_minutes: Math.max(1, Math.round(sessionDuration)),
        message_count: conversationMessages.length
      };
      
      // Call adjustment endpoint
      const response = await axios.post(`${API}/owen/pathway/adjust_after_session`, {
        user_id: userId,
        session_type: 'written',
        module_id: moduleId.toString(),
        session_data: sessionData
      });
      
      if (response.data.success && response.data.recommendations && response.data.recommendations.length > 0) {
        console.log('[adjustPathway] ✅ Pathway adjusted. Recommendations:', response.data.recommendations);
        
        // Update recommendations state for UI
        setRecommendations(response.data.recommendations);
        
        // Show subtle notification
        toast.success(`🎯 Owen a ajusté votre parcours personnalisé`, {
          duration: 3000,
          position: 'bottom-right'
        });
      }
      
    } catch (error) {
      // Non-blocking: log but don't show error to user
      console.log('[adjustPathway] Non-critical error:', error.message);
    }
  };

  const switchToVocal = () => {
    navigate(`/coach-vocal?moduleId=${moduleId}`);
  };

  // 🎨 Auto-scroll to top on page load for better UX
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Removed blocking loader - UI displays instantly now

  // =========================================================================
  // 🔒 P0 UX MOA (21 Jan 2026): EARLY RETURN — RENDU EXCLUSIF
  // =========================================================================
  // Si sessions en cours de chargement → écran de chargement (pas d'UI IA)
  if (sessionsLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF8EA] via-[#F7E9FF] to-[#E6D8FF] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#8B5CF6] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#554C6C] font-medium">Chargement...</p>
        </div>
      </div>
    );
  }
  
  // Si sessions épuisées → page recharge EXCLUSIVE (pas d'UI IA visible)
  if (sessionsExhausted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFF8EA] via-[#F7E9FF] to-[#E6D8FF] flex items-center justify-center p-4">
        <SessionsExhaustedModal 
          isOpen={true}
          onClose={() => navigate('/user-dashboard')}
        />
      </div>
    );
  }
  // =========================================================================

  return (
    <div data-testid="ecrit-page" className="min-h-screen bg-gradient-to-br from-[#FFF8EA] via-[#F7E9FF] to-[#E6D8FF] relative overflow-x-hidden">
      {/* 🎯 GOUVERNANCE PÉDAGOGIQUE: Bandeau objectif actif — masqué quand clavier ouvert */}
      {moduleId && !isKeyboardVisible && (
          <ActiveObjectiveBanner 
            moduleId={parseInt(moduleId)} 
            onObjectiveLoaded={handleObjectiveLoaded}
          />
      )}
      
      {/* Navbar Sticky - Premium Glass */}
      <motion.nav 
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="sticky top-0 left-0 right-0 z-50 bg-white/30 backdrop-blur-xl shadow-lg border-b border-white/20"
      >
        <div className="max-w-5xl mx-auto px-3 sm:px-6 lg:px-8 py-2 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-4 min-w-0">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(`/module-v2/${moduleId}`)}
                className="p-2 hover:bg-white/40 rounded-full transition-all duration-300 flex-shrink-0"
              >
                <ArrowLeft className="w-5 h-5 text-[#3B1E73]" />
              </motion.button>
              <div className="min-w-0">
                <h1 className="text-base sm:text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#3B1E73] to-[#8B5CF6]">
                  Mode Ecrit
                </h1>
                <p className="text-xs sm:text-sm text-[#554C6C] truncate">Owen • {module?.title}</p>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex items-center gap-2">
              {/* Owen 360° Recommendations Trigger */}
              {recommendations.length > 0 && (
                <div className="relative">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowRecommendations(!showRecommendations)}
                    className="relative flex items-center space-x-2 px-3 py-2 bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-300/30 text-purple-700 rounded-full transition-all duration-300 hover:shadow-lg"
                  >
                    <Brain className="w-4 h-4" />
                    <span className="text-xs font-semibold hidden sm:inline">Owen 360°</span>
                    <motion.div
                      animate={{ scale: [1, 1.2, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-r from-yellow-400 to-orange-400 rounded-full border-2 border-white"
                    />
                  </motion.button>
                  
                  {/* Recommendations Popover */}
                  <AnimatePresence>
                    {showRecommendations && (
                      <motion.div
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 top-14 w-80 bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-purple-200/50 p-4 z-50"
                      >
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-purple-200/30">
                          <Sparkles className="w-4 h-4 text-purple-600" />
                          <h3 className="text-sm font-bold text-purple-900">Suggestions Owen 360°</h3>
                        </div>
                        
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          {recommendations.map((rec, index) => (
                            <motion.div
                              key={rec.module_id}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: index * 0.1 }}
                              onClick={() => {
                                setShowRecommendations(false);
                                navigate(`/module-v2/${rec.module_id}`);
                              }}
                              className="p-3 rounded-xl bg-gradient-to-br from-purple-50/50 to-pink-50/50 border border-purple-200/30 cursor-pointer hover:shadow-lg hover:scale-[1.02] transition-all duration-300"
                            >
                              <div className="flex items-start gap-2 mb-1">
                                <div className={clsx(
                                  "flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold",
                                  rec.type === "emotion_based" && "bg-pink-500 text-white",
                                  rec.type === "progression_based" && "bg-purple-500 text-white",
                                  rec.type === "deblocage" && "bg-orange-500 text-white"
                                )}>
                                  {Math.round(rec.relevance_score * 100)}
                                </div>
                                <div className="flex-1">
                                  <h4 className="text-xs font-bold text-purple-900 leading-tight">{rec.module_title}</h4>
                                  <p className="text-xs text-purple-700/80 mt-1">{rec.reason}</p>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                        
                        <div className="mt-3 pt-3 border-t border-purple-200/30 text-center">
                          <button
                            onClick={() => {
                              setShowRecommendations(false);
                              navigate('/training');
                            }}
                            className="text-xs text-purple-600 hover:text-purple-800 font-semibold transition-colors"
                          >
                            Voir toutes les recommandations →
                          </button>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
              
              {/* Switch to Vocal Mode */}
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(139,92,246,0.3)' }}
                whileTap={{ scale: 0.95 }}
                onClick={() => navigate(`/coach-vocal?moduleId=${moduleId}`)}
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-gradient-to-r from-[#8B5CF6] to-[#C084FC] text-white rounded-full transition-all duration-300"
              >
                <Mic className="w-4 h-4" />
                <span className="text-xs font-semibold hidden sm:inline">Mode Vocal</span>
              </motion.button>
              
              {/* End Session Button (only show if conversation has started) */}
              {messages.length > 2 && (
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(34,197,94,0.3)' }}
                  whileTap={{ scale: 0.95 }}
                  onClick={endWrittenSession}
                  disabled={isTyping}
                  className={clsx(
                    'flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 rounded-full transition-all duration-300 text-xs font-semibold',
                    isTyping
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-[0_0_15px_rgba(34,197,94,0.3)] hover:from-green-600 hover:to-emerald-700'
                  )}
                >
                  <TrendingUp className="w-4 h-4" />
                  <span className="hidden sm:inline">Terminer</span>
                </motion.button>
              )}
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Main Content */}
      <div data-testid="ecrit-content" className="py-1 sm:py-4 px-1 sm:px-4 lg:px-6 lg:flex lg:items-center lg:justify-center">
        {/* 🔧 FIX MOBILE: Utilise classe CSS avec dvh fallback */}
        <div className="w-full max-w-7xl mx-auto ecrit-panel-height">{/* ✨ max-w-7xl (1792px), hauteur maximale, centrage vertical */}
          {/* Central Chat Card - Glassmorphism Premium Agrandi */}
          <motion.div 
            data-testid="ecrit-card"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.7, ease: 'easeOut' }}
            className="h-full bg-white/40 backdrop-blur-xl rounded-3xl border border-[#E0BFFF]/20 shadow-[0_0_60px_rgba(180,100,255,0.2)] flex flex-col lg:min-h-[650px]"
          >
            {/* Header */}
            <div className="p-4 sm:p-6 border-b border-[#E0BFFF]/20 flex-shrink-0">
              <h2 className="text-2xl sm:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#3B1E73] to-[#8B5CF6]">
                Conversation avec Owen
              </h2>
              <p className="text-xs sm:text-sm text-[#554C6C] italic mt-1">
                Partagez votre situation et recevez un coaching personnalisé
              </p>
            </div>

            {/* Chat Messages Area - Maximized avec Padding Optimisé */}
            <div data-testid="ecrit-messages-area" className="flex-1 overflow-hidden p-3 sm:p-6 lg:p-10 ecrit-messages-area">
              <ScrollToBottom 
                className="h-full conversation-scroll" 
                scrollViewClassName="scroll-smooth"
                followButtonClassName="scroll-to-bottom-button"
                mode="bottom"
              >
                {/* Difficulty Selector */}
                {showDifficultySelector && scenario && scenario.difficulty_levels && (
                  <motion.div 
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-3 mb-4"
                  >
                    <div className="bg-white/50 backdrop-blur-md rounded-2xl p-4 border border-[#8B5CF6]/20">
                      <h3 className="text-lg font-bold text-[#3B1E73] mb-3">Choisissez votre niveau</h3>
                      <div className="space-y-2">
                        {scenario.difficulty_levels.map((level, idx) => (
                          <motion.button
                            key={idx}
                            whileHover={{ scale: 1.02, boxShadow: '0 0 20px rgba(139,92,246,0.3)' }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => handleDifficultyChoice(idx)}
                            className="w-full text-left p-4 bg-gradient-to-r from-[#E9D8FD] to-[#F0D9FF] hover:from-[#D1BBFF] hover:to-[#E9D8FD] rounded-xl transition-all duration-300 border border-[#8B5CF6]/20"
                          >
                            <p className="font-semibold text-[#3B1E73] mb-1">{level.level}</p>
                            <p className="text-xs text-[#554C6C]">{level.description}</p>
                          </motion.button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Scenario Loading Indicator - Non-blocking */}
                {scenarioLoading && messages.length === 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                    className="flex justify-center items-center py-12"
                  >
                    <div className="bg-gradient-to-br from-[#EDE8FF] to-[#DDD0FF] border border-[#8B5CF6]/30 px-8 py-6 rounded-3xl shadow-lg">
                      <div className="flex flex-col items-center gap-4">
                        <Loader2 className="w-10 h-10 text-[#8B5CF6] animate-spin" />
                        <div className="text-center">
                          <p className="text-[#3B1E73] font-semibold text-lg mb-1">Préparation du scénario...</p>
                          <p className="text-[#554C6C] text-sm">Owen crée une situation immersive pour vous</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Messages - Maximized avec Largeur Augmentée */}
                <div className="space-y-6">
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                      className={clsx(
                        'flex',
                        message.role === 'user' ? 'justify-end' : 'justify-start'
                      )}
                    >
                      <div
                        className={clsx(
                          'px-5 py-4 rounded-3xl shadow-lg transition-all duration-300 hover:shadow-xl',
                          message.role === 'user'
                            ? 'max-w-[85%] sm:max-w-[80%] lg:max-w-[75%] bg-gradient-to-br from-[#FFF2D0] to-[#FFE8A0] text-[#3B1E73] border border-[#FFD86F]/40'
                            : 'w-full bg-gradient-to-br from-[#EDE8FF] to-[#DDD0FF] text-[#1A0F3B] border border-[#8B5CF6]/30'
                        )}
                      >
                        {message.role === 'assistant' && (
                          <div className="flex items-center gap-2 mb-3 opacity-90">
                            <div className="w-7 h-7 bg-gradient-to-br from-[#8B5CF6] to-[#C084FC] rounded-full flex items-center justify-center shadow-md">
                              <User className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-bold text-xs sm:text-sm">Owen</span>
                          </div>
                        )}
                        <div className="leading-relaxed text-sm sm:text-base break-words overflow-wrap-anywhere ecrit-markdown">
                          <OwenMarkdown>
                            {message.content || ''}
                          </OwenMarkdown>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                  
                  {/* Typing Indicator — visible TANT QUE le streaming n'affiche pas de texte */}
                  {/* Couvre le gap entre le clic envoi et l'arrivée du premier chunk */}
                  {isTyping && !(isStreaming && streamedText) && (
                    <motion.div
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="flex justify-start"
                    >
                      <div className="bg-[#EDE8FF] border border-[#8B5CF6]/20 px-5 py-3 rounded-3xl flex items-center gap-2">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-[#8B5CF6] rounded-full animate-bounce" style={{animationDelay: '0ms'}}></div>
                          <div className="w-2 h-2 bg-[#8B5CF6] rounded-full animate-bounce" style={{animationDelay: '150ms'}}></div>
                          <div className="w-2 h-2 bg-[#8B5CF6] rounded-full animate-bounce" style={{animationDelay: '300ms'}}></div>
                        </div>
                        <span className="text-xs text-[#554C6C]">Owen réfléchit...</span>
                      </div>
                    </motion.div>
                  )}
                  
                  {/* Streaming Message Display */}
                  {isStreaming && streamedText && (
                    <motion.div
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4 }}
                      className="flex justify-start"
                    >
                      <div className="w-full px-5 py-3 rounded-3xl shadow-sm bg-[#EDE8FF] text-[#1A0F3B] border border-[#8B5CF6]/20">
                        <div className="flex items-center gap-2 mb-2 opacity-80">
                          <div className="w-6 h-6 bg-gradient-to-br from-[#8B5CF6] to-[#C084FC] rounded-full flex items-center justify-center">
                            <User className="w-3 h-3 text-white" />
                          </div>
                          <span className="font-semibold text-xs">Owen</span>
                          <span className="text-xs text-[#8B5CF6] ml-auto flex items-center gap-1">
                            <Loader2 className="w-3 h-3 animate-spin" />
                            écrit...
                          </span>
                        </div>
                        <div className="leading-relaxed text-sm break-words overflow-wrap-anywhere ecrit-markdown">
                          <OwenMarkdown>
                            {streamedText}
                          </OwenMarkdown>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </div>
                {/* Auto-scroll anchor */}
                <div ref={messagesEndRef} />
              </ScrollToBottom>
            </div>

            {/* Input Area */}
            <div 
              data-testid="ecrit-input-area"
              className="p-2 sm:p-5 lg:p-6 flex-shrink-0 sticky bottom-0 z-50 bg-white/50 backdrop-blur-xl border-t border-[#E0BFFF]/30 rounded-b-3xl"
            >
              <div className="flex items-end gap-3 sm:gap-4">
                <div className="flex-1 bg-white/60 backdrop-blur-md rounded-2xl sm:rounded-full border border-[#E0BFFF]/40 shadow-lg focus-within:shadow-[0_0_25px_rgba(139,92,246,0.4)] transition-all duration-300">
                  <Textarea
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Écrivez votre réponse ou posez une question à Owen..."
                    disabled={isTyping || showDifficultySelector}
                    className="resize-none border-0 bg-transparent focus:ring-0 text-sm sm:text-base lg:text-lg text-[#3B1E73] placeholder:text-[#8B5CF6]/50 px-5 sm:px-6 py-4 max-h-40"
                    rows={1}
                    style={{ minHeight: '60px' }}
                  />
                </div>
                
                {/* Send Button - Agrandi pour Desktop */}
                <motion.button
                  whileHover={{ scale: 1.08, boxShadow: '0 0 30px rgba(139,92,246,0.6)' }}
                  whileTap={{ scale: 0.92 }}
                  onClick={sendMessage}
                  disabled={!userInput.trim() || isTyping || showDifficultySelector}
                  className={clsx(
                    'w-14 h-14 sm:w-16 sm:h-16 lg:w-18 lg:h-18 rounded-full transition-all duration-300 flex items-center justify-center flex-shrink-0',
                    !userInput.trim() || isTyping || showDifficultySelector
                      ? 'bg-gray-300/50 cursor-not-allowed backdrop-blur-sm'
                      : 'bg-gradient-to-r from-[#8B5CF6] to-[#FFD86F] shadow-[0_0_25px_rgba(139,92,246,0.5)] hover:shadow-[0_0_35px_rgba(139,92,246,0.7)]'
                  )}
                >
                  {isTyping ? (
                    <Loader2 className="w-6 h-6 sm:w-7 sm:h-7 text-white animate-spin" />
                  ) : (
                    <Send className="w-6 h-6 sm:w-7 sm:h-7 text-white" />
                  )}
                </motion.button>
              </div>
              
              {/* Instructions - Masquées sur mobile (pas de clavier physique) */}
              <p className="hidden sm:block text-xs text-[#554C6C]/70 text-center mt-2">
                <kbd className="px-2 py-1 bg-white/40 rounded text-[10px]">Entrée</kbd> pour envoyer • 
                <kbd className="px-2 py-1 bg-white/40 rounded text-[10px] ml-1">Shift + Entrée</kbd> pour nouvelle ligne
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default EntrainementEcrit;
